import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit';
import { RootState } from './store';
import { getTimezone } from '../services/userService';
import { getTimezoneLong } from '../helpers/date';
import type { TeamMember, User } from '../types'
import { UserAccessLevels } from '../enums'

export { UserAccessLevels }
export type { User }

type UserState = User | null;

const initialState: UserState = null;

const sliceName = 'currentUser';

export const fetchUserTimezone = createAsyncThunk(`${sliceName}/fetchTimezone`, (currentUser: User | null) => {
  if (!currentUser || !currentUser.customer_number) {
    throw new Error('Current user not found');
  }

  return getTimezone(currentUser.customer_number).then(timezone => timezone ? getTimezoneLong(timezone) : undefined);
});

export const userSlice = createSlice({
  name: sliceName,
  initialState: initialState as UserState,
  reducers: {
    login: (_, action) => {
      return action.payload;
    },
    logout: () => {
      return initialState;
    }
  },
  extraReducers(builder) {
    builder.addCase(fetchUserTimezone.fulfilled, (state, action) => {
      if (!state) {
        return;
      }
      state.timezone = action.payload ?? undefined;
    });
  }
});

export const { login, logout } = userSlice.actions;

export const selectCurrentUser = (state: RootState) => state.currentUser;

export const selectCurrentUserCustomers = (state: RootState) => state.currentUser?.users_group_members;

export const selectUserId = createSelector(selectCurrentUser, (user) => user?.user_id);

export const selectUserTimezone = createSelector(selectCurrentUser, (u) => u?.timezone);

export const getFullNameOrEmail = (user: User | TeamMember | undefined) => user ? (user?.firstname && user?.lastname ? `${user?.firstname} ${user?.lastname}` : user?.email ?? 'No Name') : 'No Name';

export const selectIsAdminOrOwner = createSelector(selectCurrentUser, (user) => user?.access_level === UserAccessLevels.Admin || user?.access_level === UserAccessLevels.Owner);

export const selectIsAdmin = createSelector(selectCurrentUser, (user) => user?.access_level === UserAccessLevels.Admin);

export const selectIsOwner = createSelector(selectCurrentUser, (user) => user?.access_level === UserAccessLevels.Owner);

export default userSlice.reducer;
